'use client';

/**
 * The capabilities browser's left rail: what KIND of thing Meridian can do.
 *
 * Category is the spine rather than use case (4 buckets, too coarse to
 * navigate 361 rows) or entity (31, too fine to scan). The counts are the
 * point — "Edit 95" answers "how much of this is stuff that changes records?"
 * before the reader has clicked anything.
 *
 * Counts are computed over the role-filtered list, so they say what THIS
 * viewer can do, not what the catalog contains.
 */

import React from 'react';
import { ALL_KEY, CATEGORY_LABELS } from './labels.js';

export interface CategorySidebarProps {
  categories: readonly string[];
  counts: Record<string, number>;
  total: number;
  selected: string;
  onSelect: (category: string) => void;
}

function Row({
  label,
  count,
  isSelected,
  onClick,
}: {
  label: string;
  count: number;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isSelected ? 'true' : undefined}
      className={[
        'flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
        isSelected
          ? 'bg-primary/10 font-medium text-primary'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      ].join(' ')}
    >
      <span className="truncate">{label}</span>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{count}</span>
    </button>
  );
}

export function CategorySidebar({
  categories,
  counts,
  total,
  selected,
  onSelect,
}: CategorySidebarProps) {
  return (
    // `lg:` and up this is a rail; below it is a horizontally scrolling strip,
    // because a 31-row vertical list above the results on a phone means the
    // reader never sees a result without scrolling past the navigation.
    <nav
      aria-label="Capability categories"
      className="flex shrink-0 gap-1 overflow-x-auto border-b border-border pb-2 lg:w-52 lg:flex-col lg:overflow-x-visible lg:overflow-y-auto lg:border-b-0 lg:border-r lg:pb-0 lg:pr-3"
    >
      <div className="flex gap-1 lg:w-full lg:flex-col">
        <Row
          label="All capabilities"
          count={total}
          isSelected={selected === ALL_KEY}
          onClick={() => onSelect(ALL_KEY)}
        />
        {categories.map((category) => (
          <Row
            key={category}
            label={CATEGORY_LABELS[category] ?? category}
            count={counts[category] ?? 0}
            isSelected={selected === category}
            onClick={() => onSelect(category)}
          />
        ))}
      </div>
    </nav>
  );
}
